import ErrorBoundary from '@app/components/ui/ErrorBoundary';
import { getAntdLocaleByLanguage, getDayjsLocaleByLanguage } from '@app/i18n/helpers';
import { ChangelogProvider } from '@context/changelog/ChangelogContext';
import FeatureProvider from '@context/features/FeaturesProvider';
import { MessageProvider } from '@context/message';
import { ModalProvider } from '@context/modal-provider';
import NotificationProvider from '@context/notifications/NotificationProvider';
import UiProvider from '@context/ui/UiProvider';
import { TableLabelsProvider } from '@konnecta/generic-table';
import { refreshPlanningSessionAfterMutation } from '@app/features/planningSession/planningSessionInvalidation';
import { MutationCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { App as AntdApp, ConfigProvider } from 'antd';
import { Suspense, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet } from 'react-router-dom';
import { useAppTableLabels } from './hooks/useAppTableLabels';
import { appTheme } from './styles/app-theme';

import dayjs from './utils/dayjs-setup';

// Annotated because `mutationCache` below closes over `queryClient`, and without a type here
// that self-reference makes the inferred type circular.
const queryClient: QueryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
  // If The whole project uses Tanstack we can handled global errors for both Mutations and Queries.
  // This ensures that we have a consistent error handling and reporting.

  // queryCache: new QueryCache({
  //   onError: (error, query) => {
  //     // console.log('query cache error', error);

  //     if (error.message) {
  //       handleGlobalError(error.message, query.queryKey.toString());
  //     }
  //   },
  // }),
  // mutationCache: new MutationCache({
  //   onError: (error, _, __, mutation) => {
  //     if (error.message) {
  //       handleGlobalError(error.message);
  //     }
  //   },
  // }),

  // Sees every `useMutation` in the app, so the planning-session counter moves when a planner
  // edits a visit without those call sites having to know the feature exists. Writes made
  // outside `useMutation` are not covered — see `planningSessionInvalidation`.
  mutationCache: new MutationCache({
    onSuccess: () => refreshPlanningSessionAfterMutation(queryClient),
  }),
});

const App = () => {
  const { i18n } = useTranslation();
  const tableLabels = useAppTableLabels();
  // const { styles } = useStyle();

  // Keep the global dayjs locale in sync with the app language so dates render
  // localized month/day names (e.g. "23 Ιουνίου" in Greek), not just antd pickers.
  useEffect(() => {
    dayjs.locale(getDayjsLocaleByLanguage(i18n.language));
  }, [i18n.language]);

  return (
    <Suspense>
      <ErrorBoundary>
        <ConfigProvider
          theme={appTheme}
          locale={getAntdLocaleByLanguage(i18n.language)}
          // button={{ className: styles.linearGradientButton }}
        >
          <AntdApp>
            <QueryClientProvider client={queryClient}>
              {/* I am unable to npm i react-query-devtools cause of npm peer deps issues. Those should be resolved first */}
              {/* <ReactQueryDevtools initialIsOpen={false} /> */}

              <TableLabelsProvider value={tableLabels}>
                <MessageProvider>
                  <ModalProvider>
                    <NotificationProvider>
                      <FeatureProvider>
                        <UiProvider>
                          <ChangelogProvider>
                            <Outlet />
                          </ChangelogProvider>
                        </UiProvider>
                      </FeatureProvider>
                    </NotificationProvider>
                  </ModalProvider>
                </MessageProvider>
              </TableLabelsProvider>
            </QueryClientProvider>
          </AntdApp>
        </ConfigProvider>
      </ErrorBoundary>
    </Suspense>
  );
};

export { App };
